import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-scroll";

const slugify = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const PageNav = ({
  data,
  allowedTitles,
  isFlush = false,
  flushOffset = "",
  className = "",
}) => {
  const [isFixed, setIsFixed] = useState(false);
  const [triggerOffset, setTriggerOffset] = useState(0);
  const navRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateTrigger = () => {
      if (!navRef.current) return;
      const rect = navRef.current.getBoundingClientRect();
      setTriggerOffset(rect.top + window.scrollY);
    };
    updateTrigger();
    window.addEventListener("resize", updateTrigger);
    return () => window.removeEventListener("resize", updateTrigger);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const shouldFix = triggerOffset > 0 && window.scrollY >= triggerOffset;
      setIsFixed((prev) => {
        if (prev !== shouldFix) {
          window.dispatchEvent(
            new CustomEvent("pagenav:toggle", {
              detail: { active: shouldFix },
            })
          );
        }
        return shouldFix;
      });
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.dispatchEvent(
        new CustomEvent("pagenav:toggle", { detail: { active: false } })
      );
    };
  }, [triggerOffset]);

  const handleSetActive = (to) => {
    if (!containerRef.current) return;
    const target = containerRef.current.querySelector(`[data-target="${to}"]`);
    if (target) {
      const { offsetLeft, offsetWidth } = target;
      const { scrollLeft, clientWidth } = containerRef.current;
      const isVisible =
        offsetLeft >= scrollLeft &&
        offsetLeft + offsetWidth <= scrollLeft + clientWidth;
      if (!isVisible) {
        containerRef.current.scrollTo({
          left: offsetLeft - clientWidth / 2 + offsetWidth / 2,
          behavior: "smooth",
        });
      }
    }
  };

  const navLinks = allowedTitles
    ? allowedTitles
        .map((title) =>
          data.page.find((link) => link.title === title || link.id === title)
        )
        .filter(Boolean)
    : data.page;

  const resolvedWidth = isFlush ? "max-w-7xl" : "max-w-7xl";
  const resolvedPadding = "px-4 sm:px-6 lg:px-8";
  const resolvedInnerSpacing = isFlush ? "py-2" : "py-4";

  return (
    <>
      <nav
        ref={navRef}
        className={`w-full transition-all duration-500 ${
          isFixed
            ? "fixed top-0 left-0 z-[320] bg-white3 shadow-lg px-5 py-2"
            : `relative ${isFlush ? "bg-transparent" : "bg-ofWhite"} ${
                isFlush ? flushOffset : ""
              }`
        } ${className}`}
      >
        <div className={`${resolvedWidth} mx-auto ${resolvedPadding}`}>
          <div
            ref={containerRef}
            className={`flex items-center gap-4 ${resolvedInnerSpacing} text-gray-700 overflow-x-auto scrollbar-hide justify-center`}
          >
            {navLinks.map((link, index) => (
              <React.Fragment key={`${link.title}-${index}`}>
                <Link
                  to={link.id ? link.id : link.title ? slugify(link.title) : ""}
                  smooth={true}
                  duration={800}
                  offset={-80}
                  spy={true}
                  onSetActive={handleSetActive}
                  activeClass="text-red font-semibold"
                  className="cursor-pointer hover:text-red transition-colors whitespace-nowrap"
                  data-target={
                    link.id ? link.id : link.title ? slugify(link.title) : ""
                  }
                >
                  {link.title === "Secured Business Loan (SBL)"
                    ? "Secured Business Loan"
                    : link.title}
                </Link>
                {index < navLinks.length - 1 && <span className="mx-2">|</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};

export default PageNav;
